import type {
    NativeInvoiceFooterAlign,
    NativeInvoiceLabelKey,
} from "../constants/nativeInvoice";
import type { NativeInvoiceDocumentPage } from "./nativeInvoicePagination";
import { escapeHtml } from "./escapeHtml";

export interface NativeInvoiceDocumentPartyView {
    name: string;
    fullName: string;
    company: string;
    email: string;
    showEmail: boolean;
    phoneNumber: string;
    taxId: string;
    addressLines: string[];
    showLabel: boolean;
    sender: string;
}

export interface NativeInvoiceDocumentLineView {
    productOrService: string;
    description: string;
    quantity: string;
    unitAmountFormatted: string;
    amountFormatted: string;
    // Formatted per-line Tax/Discount/Deposit values. Always populated
    // (formatted from the line's raw value, or "0" when the line has none)
    // regardless of column visibility, matching unitAmountFormatted/
    // amountFormatted — the columns object below decides whether the cell is
    // actually rendered.
    taxFormatted: string;
    discountFormatted: string;
    depositFormatted: string;
}

function infoBlock(label: string, value: string): string {
    if (!value) {
        return "";
    }
    return `<div class="infoBlock"><div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(value)}</div></div>`;
}

function partyMeta(value: string): string {
    return `<div class="partyMeta">${escapeHtml(value)}</div>`;
}

function hasPartyContent(party: NativeInvoiceDocumentPartyView): boolean {
    return Boolean(
        party.name ||
        party.fullName ||
        party.company ||
        party.sender ||
        party.showEmail ||
        party.addressLines.length > 0 ||
        party.phoneNumber ||
        party.taxId,
    );
}

function partyColumn(
    label: string,
    party: NativeInvoiceDocumentPartyView,
    taxIdLabel: string,
): string {
    if (!hasPartyContent(party)) {
        return "";
    }
    const heading = party.showLabel
        ? `<div class="label">${escapeHtml(label)}</div>`
        : "";
    const taxId = party.taxId ? partyMeta(`${taxIdLabel} ${party.taxId}`) : "";
    return `<div class="partyColumn">${heading}<div class="partyBlock"><div class="partyName">${escapeHtml(party.name)}</div>${party.fullName ? partyMeta(party.fullName) : ""}${party.company ? partyMeta(party.company) : ""}${party.sender ? partyMeta(party.sender) : ""}${party.showEmail ? partyMeta(party.email) : ""}${party.addressLines.map(partyMeta).join("")}${party.phoneNumber ? partyMeta(party.phoneNumber) : ""}${taxId}</div></div>`;
}

function totalRow(label: string, value: string, extraClass = ""): string {
    return `<div class="totalRow${extraClass}"><span class="totalRowLabel">${escapeHtml(label)}</span><span class="totalRowValue">${escapeHtml(value)}</span></div>`;
}

// Which items-table columns to render, driven by fieldSettings (Fields &
// visibility → Items); hiding one drops both its header cell and every
// line's cell for that column, so the table never renders a column of empty
// cells. `tax` / `discount` / `deposit` additionally require the "Display
// column per line item" toggle and per-line calculation, and are independent
// of the Totals rows of the same name. Column print order when all are
// visible: Item, Quantity, Rate, Tax, Discount, Deposit, Amount.
export interface NativeInvoiceItemsTableColumnVisibility {
    item: boolean;
    quantity: boolean;
    rate: boolean;
    amount: boolean;
    tax: boolean;
    discount: boolean;
    deposit: boolean;
}

function renderLineRow(
    line: NativeInvoiceDocumentLineView,
    columns: NativeInvoiceItemsTableColumnVisibility,
): string {
    const name = line.productOrService || line.description;
    const description =
        line.productOrService && line.description
            ? `<div class="lineDescription">${escapeHtml(line.description)}</div>`
            : "";
    const itemCell = columns.item
        ? `<td><div class="lineName">${escapeHtml(name)}</div>${description}</td>`
        : "";
    const quantityCell = columns.quantity
        ? `<td class="numericCol narrowCol">${escapeHtml(line.quantity)}</td>`
        : "";
    const rateCell = columns.rate
        ? `<td class="numericCol">${escapeHtml(line.unitAmountFormatted)}</td>`
        : "";
    const taxCell = columns.tax
        ? `<td class="numericCol narrowCol">${escapeHtml(line.taxFormatted)}</td>`
        : "";
    const discountCell = columns.discount
        ? `<td class="numericCol narrowCol">${escapeHtml(line.discountFormatted)}</td>`
        : "";
    const depositCell = columns.deposit
        ? `<td class="numericCol narrowCol">${escapeHtml(line.depositFormatted)}</td>`
        : "";
    const amountCell = columns.amount
        ? `<td class="numericCol">${escapeHtml(line.amountFormatted)}</td>`
        : "";
    // Column print order: Item, Quantity, Rate, Tax, Discount, Deposit, Amount.
    return `<tr>${itemCell}${quantityCell}${rateCell}${taxCell}${discountCell}${depositCell}${amountCell}</tr>`;
}

export interface NativeInvoiceDocumentPageRenderInput {
    page: NativeInvoiceDocumentPage<NativeInvoiceDocumentLineView>;
    labels: Record<NativeInvoiceLabelKey, string>;
    logoUrl: string | null;
    monogram: string;
    accentColor: string;
    invoiceNumber: string;
    poNumber: string;
    // Resolved Terms dropdown display string (e.g. "Net 30"), newly printed
    // on the info grid per PRO-12178. Distinct from `terms` below (the
    // free-text "Payment instructions" field printed in the bottom block).
    termTypeLabel: string;
    invoiceDate: string;
    dueDate: string;
    from: NativeInvoiceDocumentPartyView;
    to: NativeInvoiceDocumentPartyView;
    showSubtotal: boolean;
    showTax: boolean;
    showDiscount: boolean;
    showDeposit: boolean;
    showTotalDue: boolean;
    itemsTableColumns: NativeInvoiceItemsTableColumnVisibility;
    subtotalFormatted: string;
    taxFormatted: string;
    discountFormatted: string;
    depositFormatted: string;
    totalFormatted: string;
    terms: string;
    note: string;
    payUrl: string | null;
    footerAlign: NativeInvoiceFooterAlign;
    showPagination: boolean;
    // Whether the top-left "INVOICE" heading text prints. The rest of the
    // masthead (logo/monogram, info grid) is unaffected - see
    // NativeInvoicePdfFieldSettingsApplyResult.showHeading.
    showHeading: boolean;
}

export function renderNativeInvoiceDocumentPage({
    page,
    labels,
    logoUrl,
    monogram,
    accentColor,
    invoiceNumber,
    poNumber,
    termTypeLabel,
    invoiceDate,
    dueDate,
    from,
    to,
    showSubtotal,
    showTax,
    showDiscount,
    showDeposit,
    showTotalDue,
    itemsTableColumns,
    subtotalFormatted,
    taxFormatted,
    discountFormatted,
    depositFormatted,
    totalFormatted,
    terms,
    note,
    payUrl,
    footerAlign,
    showPagination,
    showHeading,
}: NativeInvoiceDocumentPageRenderInput): string {
    const hasTerms = Boolean(terms);
    const hasMemo = Boolean(note);
    const isSingleBottomColumn = hasTerms !== hasMemo;
    const singleClass = isSingleBottomColumn ? " bottomRowSingle" : "";
    const brand = logoUrl
        ? `<img class="brandLogo" src="${escapeHtml(logoUrl)}" alt="" />`
        : `<div class="brandMonogram">${escapeHtml(monogram)}</div>`;
    const heading = showHeading
        ? `<h1 class="invoiceHeading">${escapeHtml(labels.invoiceHeading)}</h1>`
        : "";
    const masthead = page.showMasthead
        ? `<header class="masthead">${heading}${brand}</header><section class="infoGrid"><div class="metaColumn">${infoBlock(labels.invoiceNumber, invoiceNumber)}${infoBlock(labels.poNumber, poNumber)}${infoBlock(labels.termsType, termTypeLabel)}${infoBlock(labels.invoiceDate, invoiceDate)}${infoBlock(labels.dueDate, dueDate)}</div>${partyColumn(labels.from, from, labels.taxId)}${partyColumn(labels.to, to, labels.taxId)}</section>`
        : "";
    const headerCells = [
        itemsTableColumns.item ? `<th>${escapeHtml(labels.item)}</th>` : "",
        itemsTableColumns.quantity
            ? `<th class="numericCol narrowCol">${escapeHtml(labels.quantity)}</th>`
            : "",
        itemsTableColumns.rate
            ? `<th class="numericCol">${escapeHtml(labels.rate)}</th>`
            : "",
        itemsTableColumns.tax
            ? `<th class="numericCol narrowCol">${escapeHtml(labels.taxColumn)}</th>`
            : "",
        itemsTableColumns.discount
            ? `<th class="numericCol narrowCol">${escapeHtml(labels.discountColumn)}</th>`
            : "",
        itemsTableColumns.deposit
            ? `<th class="numericCol narrowCol">${escapeHtml(labels.depositColumn)}</th>`
            : "",
        itemsTableColumns.amount
            ? `<th class="numericCol">${escapeHtml(labels.amount)}</th>`
            : "",
    ].join("");
    const table = page.showTable
        ? `<table class="lineTable"><thead><tr>${headerCells}</tr></thead><tbody>${page.lines.map((line) => renderLineRow(line, itemsTableColumns)).join("")}</tbody></table>`
        : "";
    const payButton =
        payUrl != null
            ? `<div style="margin-top: 24px; text-align: right;"><a href="${escapeHtml(payUrl)}" style="display: inline-block; padding: 10px 20px; background-color: ${accentColor}; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600;">${escapeHtml(labels.payThisInvoice)}</a></div>`
            : "";
    const bottom =
        hasTerms || hasMemo
            ? `<section class="bottomColumns"><div class="bottomHeadingRow${singleClass}">${hasTerms ? `<div class="bottomHeading">${escapeHtml(labels.paymentInstructions)}</div>` : ""}${hasMemo ? `<div class="bottomHeading">${escapeHtml(labels.memo)}</div>` : ""}</div><div class="bottomContentRow${singleClass}">${hasTerms ? `<p class="bottomText">${escapeHtml(terms)}</p>` : ""}${hasMemo ? `<p class="bottomText">${escapeHtml(note)}</p>` : ""}</div></section>`
            : "";
    const closing = page.showClosing
        ? `<div class="totals">${showSubtotal ? totalRow(labels.subtotal, subtotalFormatted) : ""}${showTax ? totalRow(labels.tax, taxFormatted) : ""}${showDiscount ? totalRow(labels.discount, discountFormatted) : ""}${showDeposit ? totalRow(labels.deposit, depositFormatted) : ""}${showTotalDue ? totalRow(labels.totalDue, totalFormatted, ` totalRowGrand${showTax || showDiscount || showDeposit ? " totalRowGrandDivider" : ""}`) : ""}</div>${payButton}${bottom}`
        : "";
    const pagination = showPagination
        ? `<span class="documentFooterPagination">${page.pageNumber} of ${page.pageCount}</span>`
        : "";
    const footer =
        page.footer || pagination
            ? `<footer class="documentFooter" style="text-align: ${footerAlign};"><span class="documentFooterText">${escapeHtml(page.footer)}</span>${pagination}</footer>`
            : "";
    return `<div class="page"><div class="pageBody">${masthead}${table}${closing}</div>${footer}</div>`;
}
